import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../services/api';
import { getOnboardingStatus, resendVerificationEmail } from '../../services/authService';
import EmailVerificationPrompt from '../../components/EmailVerificationPrompt/EmailVerificationPrompt';
import styles from './Quiz.module.css';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { OnboardingDiagnostics } from '../../diagnostics/onboardingDiagnostics';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E'];
const FEEDBACK_DELAY = 300;

function formatElapsed(s) {
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function Quiz() {
    const { state } = useLocation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, updateUserData } = useAuth();

    const stateQuizData = state?.quizData;
    const stateKind = state?.kind;
    const stateSeasonNumber = state?.seasonNumber;
    const stateMediaTitle = state?.mediaTitle;
    const stateTmdbId = state?.tmdbId;

    const [quizData, setQuizData] = useState(stateQuizData || null);
    const [kind, setKind] = useState(stateKind || null);
    const [seasonNumber, setSeasonNumber] = useState(stateSeasonNumber || null);
    const [mediaTitle, setMediaTitle] = useState(stateMediaTitle || null);
    const [tmdbId, setTmdbId] = useState(stateTmdbId || null);
    const [loading, setLoading] = useState(!stateQuizData && !!id);
    const [loadError, setLoadError] = useState(null);
    // Re-opening an attempt after the account's one quiz before verification
    // was used: verify first (the attempt stays open).
    const [loadBlocked, setLoadBlocked] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [continuing, setContinuing] = useState(false);
    const [continueError, setContinueError] = useState(null);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    // Set when the backend refuses to score because the email isn't verified.
    // Kept separate from submitError so we can show a friendly prompt instead
    // of the red failure state — the answers are still held and re-submittable.
    const [verificationNeeded, setVerificationNeeded] = useState(null);
    const [resendState, setResendState] = useState({ status: 'idle', message: null });
    const [elapsed, setElapsed] = useState(0);
    const startRef = useRef(Date.now());
    const completedRef = useRef(false);
    const startedAttemptRef = useRef(null);
    const answeredCountRef = useRef(0);

    // Track answered questions so we can pass them to the result page for reporting
    const [answeredQuestions, setAnsweredQuestions] = useState([]);
    answeredCountRef.current = answers.length;

    useEffect(() => {
        const attempt = quizData?.attemptId;
        if (!attempt || startedAttemptRef.current === attempt) return undefined;
        startedAttemptRef.current = attempt;
        startRef.current = Date.now();
        trackEvent(ANALYTICS_EVENTS.CHALLENGE_STARTED, {
            content_id: tmdbId || attempt, content_type: kind || 'unknown', quiz_attempt_number: quizData.attemptNumber || 1,
            current_coin_tier: quizData.currentCoinTier || 'none',
        }, { onceKey: attempt });
        return () => {
            if (!completedRef.current) trackEvent(ANALYTICS_EVENTS.CHALLENGE_ABANDONED, {
                content_id: tmdbId || attempt, content_type: kind || 'unknown', questions_answered: answeredCountRef.current,
                total_questions: quizData.questions?.length || 0, time_spent_seconds: Math.round((Date.now() - startRef.current) / 1000),
            }, { onceKey: attempt });
        };
    }, [quizData?.attemptId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (stateQuizData || !id) return;

        const loadAttempt = async () => {
            try {
                const res = await apiRequest(`/quiz/attempt/${id}`, { authenticated: true });
                const data = res.data ?? res;
                setQuizData(data);
                setKind(data.kind ?? null);
                setSeasonNumber(data.seasonNumber ?? null);
                setMediaTitle(data.mediaTitle ?? null);
                setTmdbId(data.tmdbId ?? null);
            } catch (err) {
                if (err?.code === 'email_verification_required') {
                    setLoadBlocked({ email: err.payload?.email ?? user?.email ?? null, pendingCoin: err.payload?.pendingCoin ?? null });
                    return;
                }
                setLoadError(err.message || "Couldn't load your challenge. Please start again from the title page.");
            } finally {
                setLoading(false);
            }
        };

        setLoadBlocked(null);
        loadAttempt();
    }, [id, stateQuizData, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        startRef.current = Date.now();
        const intervalId = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
        return () => clearInterval(intervalId);
    }, [currentIndex]);

    if (loading) {
        return (
            <div className={styles.centered}>
                <div className={styles.spinner} />
                <p className={styles.loadingText}>Loading your challenge…</p>
            </div>
        );
    }

    if (loadBlocked) {
        const handleVerifiedContinue = async () => {
            setContinuing(true);
            setContinueError(null);
            try {
                const status = await getOnboardingStatus();
                if (!status?.emailVerified) {
                    setContinueError("We still can't see the verification. Open the link in the email we sent, then try again.");
                    return;
                }
                updateUserData?.({ emailVerified: true, ...(status.coinCount != null && { coinCount: status.coinCount }) });
                setLoading(true);
                setReloadKey((k) => k + 1);
            } catch (err) {
                setContinueError(err?.message || 'Something went wrong. Please try again.');
            } finally {
                setContinuing(false);
            }
        };
        return (
            <div className={styles.centered}>
                <EmailVerificationPrompt
                    email={loadBlocked.email}
                    pendingCoin={loadBlocked.pendingCoin}
                    variant="keep_playing"
                    onContinue={handleVerifiedContinue}
                    continuing={continuing}
                    error={continueError}
                    context={{ source_screen: 'quiz_resume' }}
                />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className={styles.centered}>
                <span style={{ fontSize: '3rem' }}>😬</span>
                <p style={{ color: '#e74c3c', textAlign: 'center' }}>{loadError}</p>
                <button className={styles.retryBtn} onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    if (!quizData) {
        navigate('/');
        return null;
    }

    if (!quizData.questions || quizData.questions.length === 0) {
        return (
            <div className={styles.centered}>
                <span style={{ fontSize: '3rem' }}>😬</span>
                <p style={{ color: '#e74c3c', textAlign: 'center' }}>
                    Couldn't load your challenge. Please try again.
                </p>
                <button className={styles.retryBtn} onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const { attemptId, questions } = quizData;
    const total = questions.length;
    const required = quizData.requiredToPass ?? Math.ceil(total * 0.6);
    const question = questions[currentIndex];

    if (!question) {
        return (
            <div className={styles.centered}>
                <span style={{ fontSize: '3rem' }}>😬</span>
                <p style={{ color: '#e74c3c', textAlign: 'center' }}>
                    Unable to load question. Please try again.
                </p>
                <button className={styles.retryBtn} onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const progress = ((currentIndex + 1) / total) * 100;
    const seasonLabel = kind === 'tv' && seasonNumber != null ? ` · Season ${seasonNumber}` : '';
    const isCaution = elapsed >= 20;

    // The verification gate is not a failure — the attempt stays open, so we
    // route it to its own friendly panel rather than the red error state.
    const handleSubmitFailure = (e) => {
        if (e?.code === 'email_verification_required') {
            setVerificationNeeded({
                email: e.payload?.email ?? user?.email ?? null,
                message: e.message,
            });
            setResendState({ status: 'idle', message: null });
            return;
        }
        // Network failures never reach the server, so the client reports them. Never answers.
        OnboardingDiagnostics.quizSubmitFailed({ error: e, attemptId: quizData?.attemptId });
        setSubmitError(e?.message || 'Failed to submit. Please try again.');
    };

    const handleResendVerification = async () => {
        setResendState({ status: 'sending', message: null });
        try {
            await resendVerificationEmail();
            setResendState({
                status: 'sent',
                message: 'Sent! Check your inbox — the link is good for 24 hours.',
            });
        } catch (e) {
            setResendState({
                status: 'error',
                message: e?.message || 'Could not send the email. Please try again shortly.',
            });
        }
    };

    const handleSelect = async (tappedIndex) => {
        if (selectedIndex !== null || submitting) return;
        setSelectedIndex(tappedIndex);

        setTimeout(async () => {
            const newAnswers = [
                ...answers,
                { questionId: question.id, selectedIndex: tappedIndex, timeSeconds: elapsed },
            ];
            setAnswers(newAnswers);

            const newAnsweredQuestions = [
                ...answeredQuestions,
                { question, selectedIndex: tappedIndex },
            ];
            setAnsweredQuestions(newAnsweredQuestions);

            setSelectedIndex(null);

            if (currentIndex < total - 1) {
                setElapsed(0);
                setCurrentIndex(i => i + 1);
                return;
            }

            // Submit
            setSubmitting(true);
            setSubmitError(null);
            setVerificationNeeded(null);
            try {
                const payload = newAnswers.map(({ questionId, selectedIndex: si }) => ({ questionId, selectedIndex: si }));
                const result = await apiRequest('/quiz/submit', {
                    method: 'POST',
                    body: { attemptId, answers: payload },
                    authenticated: !!user,
                });
                completedRef.current = true;
                const outcome = result?.data ?? result;
                const common = { content_id: tmdbId || attemptId, content_type: kind || 'unknown', score: outcome.score,
                    total_questions: outcome.totalQuestions || total, quiz_attempt_number: quizData.attemptNumber || 1,
                    time_spent_seconds: Math.round((Date.now() - startRef.current) / 1000), coin_tier: outcome.badge?.coin?.rarity };
                trackEvent(ANALYTICS_EVENTS.CHALLENGE_COMPLETED, common, { onceKey: attemptId });
                if (!outcome.passed) trackEvent(ANALYTICS_EVENTS.CHALLENGE_FAILED, common, { onceKey: attemptId });
                if (outcome.badge?.upgraded) trackEvent(ANALYTICS_EVENTS.COIN_UPGRADED, { ...common, previous_coin_tier: outcome.badge.previousRarity }, { onceKey: attemptId });
                else if (outcome.badge && !outcome.badge.alreadyHad) trackEvent(ANALYTICS_EVENTS.COIN_EARNED, common, { onceKey: attemptId });
                if (outcome.badge?.coin?.rarity?.toLowerCase() === 'legendary' && !outcome.badge.alreadyHad) trackEvent(ANALYTICS_EVENTS.LEGENDARY_EARNED, common, { onceKey: attemptId });
                // First quiz before email verification: earned, held until verified.
                if (outcome.rewardDeferred && outcome.pendingCoin) trackEvent(ANALYTICS_EVENTS.PENDING_COIN_EARNED, { ...common, coin_tier: outcome.pendingCoin.rarity }, { onceKey: attemptId });
                const completionProps = (reward) => ({
                    completion_type: reward.completionType, required_count: reward.requiredCount ?? undefined, total_entries: reward.totalEntries,
                });
                (outcome.collectionRewards || []).forEach((reward) => trackEvent(ANALYTICS_EVENTS.COLLECTION_COMPLETED, {
                    collection_id: reward.collectionId, collection_name: reward.collectionName, ...completionProps(reward),
                }, { onceKey: reward.collectionId || reward.collectionName }));
                (outcome.collectionRewards || []).filter((reward) => reward.isSeasonal).forEach((reward) => trackEvent(
                    ANALYTICS_EVENTS.SEASONAL_COLLECTION_REWARD_EARNED,
                    { collection_id: reward.collectionId, season_label: reward.seasonLabel || undefined, ...completionProps(reward) },
                    { onceKey: reward.collectionId },
                ));

                navigate('/quiz/result', {
                    state: {
                        result,
                        kind,
                        seasonNumber,
                        mediaTitle,
                        tmdbId,
                        attemptId,
                        answeredQuestions: newAnsweredQuestions,
                    },
                });
            } catch (e) {
                handleSubmitFailure(e);
            } finally {
                setSubmitting(false);
            }
        }, FEEDBACK_DELAY);
    };

    const handleRetry = async () => {
        setSubmitting(true);
        setSubmitError(null);
        setVerificationNeeded(null);
        try {
            const payload = answers.map(({ questionId, selectedIndex: si }) => ({ questionId, selectedIndex: si }));
            const result = await apiRequest('/quiz/submit', {
                method: 'POST',
                body: { attemptId, answers: payload },
                authenticated: !!user,
            });
            navigate('/quiz/result', {
                state: {
                    result,
                    kind,
                    seasonNumber,
                    mediaTitle,
                    tmdbId,
                    answeredQuestions,
                },
            });
        } catch (e) {
            handleSubmitFailure(e);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitting) {
        return (
            <div className={styles.centered}>
                <div className={styles.spinner} />
                <p className={styles.loadingText}>Submitting…</p>
            </div>
        );
    }

    if (verificationNeeded) {
        return (
            <div className={styles.centered}>
                <div className={styles.verifyCard}>
                    <span className={styles.verifyIcon}>📬</span>
                    <h2 className={styles.verifyTitle}>One quick step before your coin</h2>
                    <p className={styles.verifyBody}>
                        {verificationNeeded.message}
                    </p>
                    {verificationNeeded.email && (
                        <p className={styles.verifyEmail}>{verificationNeeded.email}</p>
                    )}
                    <p className={styles.verifyNote}>
                        Your answers are saved — verify, then submit them below. Nothing is lost.
                    </p>

                    <div className={styles.verifyActions}>
                        <button
                            className={styles.retryBtn}
                            onClick={handleResendVerification}
                            disabled={resendState.status === 'sending'}
                        >
                            {resendState.status === 'sending' ? 'Sending…' : 'Resend verification email'}
                        </button>
                        <button className={styles.verifySecondaryBtn} onClick={handleRetry}>
                            I've verified — submit my answers
                        </button>
                    </div>

                    {resendState.message && (
                        <p
                            className={
                                resendState.status === 'error'
                                    ? styles.verifyFeedbackError
                                    : styles.verifyFeedbackOk
                            }
                            role="status"
                        >
                            {resendState.message}
                        </p>
                    )}

                    <button className={styles.verifyBrowseLink} onClick={() => navigate('/discover')}>
                        Keep browsing instead →
                    </button>
                </div>
            </div>
        );
    }

    if (submitError) {
        return (
            <div className={styles.centered}>
                <span style={{ fontSize: '3rem' }}>😬</span>
                <p style={{ color: '#e74c3c', textAlign: 'center' }}>{submitError}</p>
                <button className={styles.retryBtn} onClick={handleRetry}>Try Again</button>
            </div>
        );
    }

    return (
        <div className={styles.quizPage}>
            <div className={styles.quizInner}>

                {/* ── Header ── */}
                <div className={styles.header}>
                    <div className={styles.headerTopRow}>
                        <span className={styles.eyebrow}>Verifying</span>
                        <span className={`${styles.timerPill} ${isCaution ? styles.timerCaution : ''}`}>
                            {formatElapsed(elapsed)}
                        </span>
                    </div>
                    <h2 className={styles.mediaTitle}>
                        {mediaTitle}
                        {seasonLabel && <span className={styles.seasonSuffix}>{seasonLabel}</span>}
                    </h2>
                    <p className={styles.passRequirement}>
                        {required} of {total} correct to earn the coin 🪙
                    </p>
                </div>

                {/* ── Progress ── */}
                <div className={styles.progressRow}>
                    <div className={styles.progressTrack}>
                        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                    </div>
                    <span className={styles.progressCounter}>{currentIndex + 1}/{total}</span>
                </div>

                {/* ── Question ── */}
                <p className={styles.questionText}>{question.question}</p>

                {/* ── Options ── */}
                <div className={styles.options}>
                    {question.options.map((opt, i) => {
                        const isTapped = selectedIndex === i;
                        return (
                            <button
                                key={i}
                                className={`${styles.option} ${isTapped ? styles.optionSelected : ''}`}
                                onClick={() => handleSelect(i)}
                                disabled={selectedIndex !== null}
                            >
                                <span className={`${styles.optionLabel} ${isTapped ? styles.optionLabelSelected : ''}`}>
                                    {OPTION_LABELS[i]}
                                </span>
                                <span className={styles.optionText}>{opt}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
